import 'spectre.css/dist/spectre.min.css'
import 'spectre.css/dist/spectre-icons.min.css'
import './style/index.css'

import { Client, Provider, subscriptionExchange } from '@urql/preact'
import { cacheExchange } from '@urql/exchange-graphcache'
import { makeDefaultStorage } from '@urql/exchange-graphcache/default-storage'
import { retryExchange } from '@urql/exchange-retry'
import { createClient as createSSEClient } from 'graphql-sse'

import Q from './q'
import App from './app'

const storage = makeDefaultStorage({
  idbName: 'netalyze-cache',
  maxAge: 7,
})

// --- graphcache list-update helpers -------------------------------------
// A subscription/mutation hands us a single entity; the list queries
// (offers/leads/rents) store it inside a cached collection. graphcache
// normalizes the entity record automatically, but it does NOT splice it into
// the cached list — we do that here so useQuery re-renders live.
//
// Lists come in two shapes:
//   - wrapped:  { <rootField>: { total, items: [...] } }   (offers, leads)
//   - bare:     { <rootField>: [...] }                     (rents)
// and offers/leads carry a $sortBy variable, so several variants may be cached
// at once — we update every variant via cache.inspectFields('Query').

// Run cb(variables) once per cached variant of a root field on Query.
const forEachVariant = (cache, rootField, cb) => {
  cache.inspectFields('Query')
    .filter(f => f.fieldName === rootField)
    .forEach(f => cb(f.arguments || {}))
}

// Upsert an entity into every cached { total, items } variant.
const upsertWrapped = (cache, { query, rootField, ent, idKey }) =>
  forEachVariant(cache, rootField, variables => {
    cache.updateQuery({ query, variables }, data => {
      if (!data || !data[rootField]) return data
      const items = (data[rootField].items || []).slice()
      const idx = items.findIndex(i => i && i[idKey] === ent[idKey])
      if (idx > -1) items[idx] = ent
      else { items.push(ent); if (data[rootField].total != null) data[rootField].total = items.length }
      data[rootField].items = items
      return data
    })
  })

// Upsert an entity into every cached bare-array variant.
const upsertBare = (cache, { query, rootField, ent, idKey }) =>
  forEachVariant(cache, rootField, variables => {
    cache.updateQuery({ query, variables }, data => {
      if (!data || !data[rootField]) return data
      const items = (data[rootField] || []).slice()
      const idx = items.findIndex(i => i && i[idKey] === ent[idKey])
      if (idx > -1) items[idx] = ent
      else items.push(ent)
      data[rootField] = items
      return data
    })
  })

// Remove an entity by id from every cached { total, items } variant.
const removeWrapped = (cache, { query, rootField, id, idKey }) =>
  forEachVariant(cache, rootField, variables => {
    cache.updateQuery({ query, variables }, data => {
      if (!data || !data[rootField]) return data
      const items = data[rootField].items || []
      const next = items.filter(i => i && i[idKey] !== id)
      if (next.length === items.length) return data
      data[rootField].items = next
      if (data[rootField].total != null) data[rootField].total = next.length
      return data
    })
  })

// Remove an entity by id from every cached bare-array variant.
const removeBare = (cache, { query, rootField, id, idKey }) =>
  forEachVariant(cache, rootField, variables => {
    cache.updateQuery({ query, variables }, data => {
      if (!data || !data[rootField]) return data
      const items = data[rootField] || []
      const next = items.filter(i => i && i[idKey] !== id)
      if (next.length === items.length) return data
      data[rootField] = next
      return data
    })
  })

const cache = cacheExchange({
  storage,
  // graphcache normalizes entities by id/_id. These types have no key field —
  // they are embedded (connection wrappers like {total, items}, value objects
  // like Comment/Geo/Up, or scalars-with-selection). Returning null tells
  // graphcache to embed them on their parent instead of erroring.
  keys: {
    ASInfo: () => null,
    ApiBlRecord: () => null,
    ApiBlRecords: () => null,
    ApiNetByOrg: () => null,
    ApiNetsByOrg: () => null,
    ApiOffers: () => null,
    ApiOrgByNet: () => null,
    ApiTransfers: () => null,
    Comment: () => null,
    Geo: () => null,
    Invoices: () => null,
    Leads: () => null,
    Nets: () => null,
    Nets6: () => null,
    Offers: () => null,
    Orgs: () => null,
    ProposeNet: () => null,
    Proposes: () => null,
    RequestInfo: () => null,
    Requests: () => null,
    Stat: () => null,
    StatDay: () => null,
    StatHour: () => null,
    StatMonth: () => null,
    Transfers: () => null,
    Up: () => null,
    UserStat: () => null,
    Users: () => null,
  },
  updates: {
    // The `update` subscription publishes the 5 SubUpdate types (per FRONT.md):
    // Offer | Rent | Lead | Propose | Work — on add/modify AND delete. Delete
    // events are tombstones carrying `removed: true` + the id. We splice each
    // event into the matching list query so the tables re-render live.
    //
    // Per-type config: how each union member maps to its cached list query.
    //   shape 'wrapped' -> { total, items: [...] }   shape 'bare' -> [...]
    //   alias  -> S_UPDATE aliases (responseKey -> real field) needed because
    //             dt/type/price have conflicting GraphQL types across union
    //             members and can't share a name in one selection set. We map
    //             them back so the list query's real field names are cached.
    Subscription: {
      update(parent, _args, cache) {
        const ent = parent && parent.update
        if (!ent) return

        // Resolve the list config by __typename (preferred) or, for a tombstone
        // that may omit it, by which id field is present.
        const byType = {
          Offer:   { query: Q.Q_OFFERS,       rootField: 'offers',   shape: 'wrapped', idKey: 'id' },
          Rent:    { query: Q.Q_RENT_EVERY,   rootField: 'rents',    shape: 'bare',    idKey: 'id',
            alias: { rentDt: 'dt', rentType: 'type', rentPrice: 'price' } },
          Propose: { query: Q.Q_PROPOSE_EVERY, rootField: 'proposes', shape: 'wrapped', idKey: 'id' },
          Lead:    { query: Q.Q_LEADS,        rootField: 'leads',    shape: 'wrapped', idKey: '_id',
            alias: { leadDt: 'dt', leadType: 'type' } },
          // NB: live schema has Work.id (not _id, despite FRONT.md) — verified via introspection.
          Work:    { query: Q.Q_WORK_EVERY,   rootField: 'works',    shape: 'bare',    idKey: 'id' },
        }
        const cfg = byType[ent.__typename]
        if (!cfg) {
          console.log('[CACHE-SUB] no list config for', ent.__typename, '— ignored')
          return
        }
        const id = ent[cfg.idKey]

        // Tombstone delete: remove from the list.
        if (ent.removed === true) {
          console.log('[CACHE-SUB] tombstone delete', ent.__typename, id)
          if (cfg.shape === 'wrapped') removeWrapped(cache, { query: cfg.query, rootField: cfg.rootField, id, idKey: cfg.idKey })
          else removeBare(cache, { query: cfg.query, rootField: cfg.rootField, id, idKey: cfg.idKey })
          cache.invalidate({ __typename: ent.__typename, [cfg.idKey]: id })
          return
        }

        // Un-alias the conflicting fields back to their real names so the
        // cached entity matches what the list query reads.
        let item = ent
        if (cfg.alias) {
          item = { ...ent }
          for (const [from, to] of Object.entries(cfg.alias)) {
            if (from in item) { item[to] = item[from]; delete item[from] }
          }
        }

        // Add / modify: upsert the full entity into the list.
        console.log('[CACHE-SUB] upsert', ent.__typename, id)
        if (cfg.shape === 'wrapped') upsertWrapped(cache, { query: cfg.query, rootField: cfg.rootField, ent: item, idKey: cfg.idKey })
        else upsertBare(cache, { query: cfg.query, rootField: cfg.rootField, ent: item, idKey: cfg.idKey })
      },
    },
    // The *Rem mutations return Boolean, not the entity, so graphcache can't
    // remove them from the list automatically. Splice + invalidate here so a
    // LOCAL delete is reflected even when the subscription tombstone is absent.
    Mutation: {
      offerRem(_parent, args, cache) {
        removeWrapped(cache, { query: Q.Q_OFFERS, rootField: 'offers', id: args.id, idKey: 'id' })
        cache.invalidate({ __typename: 'Offer', id: args.id })
      },
      leadRem(_parent, args, cache) {
        removeWrapped(cache, { query: Q.Q_LEADS, rootField: 'leads', id: args._id, idKey: '_id' })
        cache.invalidate({ __typename: 'Lead', _id: args._id })
      },
      rentRem(_parent, args, cache) {
        removeBare(cache, { query: Q.Q_RENT_EVERY, rootField: 'rents', id: args.id, idKey: 'id' })
        cache.invalidate({ __typename: 'Rent', id: args.id })
      },
      proposeRem(_parent, args, cache) {
        removeWrapped(cache, { query: Q.Q_PROPOSE_EVERY, rootField: 'proposes', id: args.id, idKey: 'id' })
        cache.invalidate({ __typename: 'Propose', id: args.id })
      },
      workRem(_parent, args, cache) {
        removeBare(cache, { query: Q.Q_WORK_EVERY, rootField: 'works', id: args.id, idKey: 'id' })
        cache.invalidate({ __typename: 'Work', id: args.id })
      },
    },
  },
})

// graphql-sse client handles ALL operations (queries, mutations, subscriptions).
//
// DISTINCT connections mode (singleConnection: false, the default): every
// operation is its own POST /sse request that streams its result. We MUST use
// this mode — the backend's CORS policy only allows GET/POST/OPTIONS, so
// single-connection mode (which needs PUT to reserve a stream and DELETE to
// end operations) is blocked by the browser preflight.
//
// Subscriptions still work here: a subscription is a long-lived POST stream and
// the backend publishes events onto it. The token is read dynamically per
// request via headers(), so login/logout is picked up without rebuilding the
// client (resetSSEConnection just forces in-flight streams to reconnect).
const makeSSEClient = () => createSSEClient({
  url: 'https://netalyze.dcxv.com/sse',
  headers: () => {
    const token = localStorage.getItem('beauth')
    return token ? { token } : {}
  }
})

let sseClient = makeSSEClient()

// Drop in-flight streams and build a fresh client so subscriptions/queries
// re-issue their POST with the current token. Call on login and logout.
export const resetSSEConnection = () => {
  try { sseClient.dispose() } catch (e) { /* already disposed */ }
  sseClient = makeSSEClient()
}

const client = new Client({
  url: 'https://netalyze.dcxv.com/sse',
  exchanges: [
    cache,
    retryExchange({
      initialDelayMs: 1000,
      maxDelayMs: 15000,
      randomDelay: true,
      maxNumberAttempts: Number.MAX_VALUE,
      retryIf: err => err?.networkError,
    }),
    subscriptionExchange({
      enableAllOperations: true,
      // read the module-level sseClient at call time so a post-login reset is
      // picked up by the next operation.
      forwardSubscription: (operation) => ({
        subscribe: (sink) => ({ unsubscribe: sseClient.subscribe(operation, sink) })
      }),
    }),
  ]
})

const main = () => <Provider value={client}>
  <App />
</Provider>

export default main
